import { useEffect, useRef, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { InteractiveDots } from '@/components/motion'
import { walkthroughSteps } from '@/data/about'

/**
 * Node centres in /images/screenshots/workflow-builder.png (1305 x 875), as
 * percentages of the image box, in the order of `walkthroughSteps`.
 */
const NODES = [
  { x: 43.3, y: 14.1 },
  { x: 44.1, y: 27.9 },
  { x: 44.1, y: 41.3 },
  { x: 44.1, y: 54.5 },
  { x: 42.7, y: 68.6 },
  { x: 44.1, y: 94.3 },
] as const

const BOX = { w: 14, h: 10.5 }
const STEP_MS = 2600
const EASE_OUT = [0.22, 1, 0.36, 1] as const
const EASE_IN_OUT = [0.77, 0, 0.175, 1] as const

/** Transform that moves a full-size layer so its top-left sits at (x%, y%). */
const at = (x: number, y: number) => `translate3d(${x}%, ${y}%, 0)`

/**
 * Full-width product walkthrough (the reference's 16:9 video slot): the
 * workflow-builder screenshot framed as an app window on the blue band, with
 * a cursor that steps through the contract-review pipeline and a highlight
 * that follows it. Pauses off-screen; static on the second step under
 * reduced motion.
 */
export function WalkthroughMedia() {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.35 })
  const reduce = useReducedMotion()
  const [step, setStep] = useState(1)

  useEffect(() => {
    if (reduce || !inView) return
    const id = window.setInterval(() => {
      setStep((s) => (s + 1) % walkthroughSteps.length)
    }, STEP_MS)
    return () => window.clearInterval(id)
  }, [inView, reduce])

  const node = NODES[step] ?? NODES[1]
  const boxX = node.x - BOX.w / 2
  const boxY = node.y - BOX.h / 2

  return (
    <section aria-labelledby="walkthrough-title" className="landing-container">
      <div
        ref={ref}
        className="relative overflow-hidden bg-[var(--color-landing-accent)] xl:aspect-video"
      >
        <InteractiveDots />
        <div className="relative z-10 grid h-full gap-8 p-5 sm:p-8 lg:grid-cols-[minmax(0,0.3fr)_minmax(0,1fr)] lg:items-center lg:gap-12 lg:p-[4%]">
          {/* Step list: the pipeline in words, active step in full white */}
          <div className="order-2 lg:order-1">
            <h2
              id="walkthrough-title"
              className="font-[family-name:var(--font-landing-display)] uppercase text-[1.6rem] leading-[1.4] tracking-[-0.02em] text-white"
            >
              One contract, six steps.
            </h2>
            <p className="mt-4 landing-text-body-sm text-white/80 max-w-[36ch]">
              A contract-review workflow built in the Templatean workflow builder: agents chained with your rules at every step.
            </p>
            <ol className="mt-8 grid gap-3 border-t border-white/25 pt-6">
              {walkthroughSteps.map((label, i) => (
                <li
                  key={label}
                  aria-current={i === step ? 'step' : undefined}
                  className={cn(
                    'flex items-baseline gap-3 text-base font-semibold transition-colors duration-300',
                    i === step ? 'text-white' : 'text-white/75'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'h-2 w-2 shrink-0 self-center bg-[#ff00f2] transition-opacity duration-300',
                      i === step ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="font-[family-name:var(--font-landing-mono)] text-xs tabular-nums" aria-hidden>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {label}
                </li>
              ))}
            </ol>
          </div>

          {/* App window */}
          <div className="order-1 lg:order-2 border border-black/10 bg-white shadow-[0_40px_80px_-30px_rgb(5_0_90_/_0.6)]">
            <div className="flex h-8 items-center justify-between gap-4 border-b border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] px-3">
              <span className="flex items-center gap-2 text-xs font-semibold text-[var(--color-landing-gray-700)]">
                <span aria-hidden className="h-2 w-2 bg-[var(--color-landing-accent)]" />
                Contract Review Workflow
              </span>
              <span className="font-[family-name:var(--font-landing-mono)] text-[11px] tabular-nums text-[var(--color-landing-gray-700)]" aria-hidden>
                Step {step + 1} / {walkthroughSteps.length}
              </span>
            </div>
            <div className="relative overflow-hidden">
              <img
                src="/images/screenshots/workflow-builder.png"
                alt="The Templatean workflow builder showing a contract review workflow: contract upload, Clara, Clara, Justice, a risk level check and Atticus, with the node properties panel open."
                width={1305}
                height={875}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full"
              />

              {/* Highlight follows the cursor with a short lag */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                initial={false}
                animate={{ transform: at(boxX, boxY) }}
                transition={reduce ? { duration: 0 } : { duration: 0.6, delay: 0.45, ease: EASE_OUT }}
              >
                <div
                  className="absolute left-0 top-0 border-2 border-[#ff00f2] bg-[#ff00f2]/[0.06]"
                  style={{ width: `${BOX.w}%`, height: `${BOX.h}%` }}
                />
                <div
                  className="absolute top-0 hidden whitespace-nowrap bg-[var(--color-landing-primary)] px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white sm:block"
                  style={{ left: `${BOX.w + 1.2}%`, marginTop: '0.35rem' }}
                >
                  {walkthroughSteps[step]}
                </div>
              </motion.div>

              {/* Cursor */}
              {!reduce && (
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  initial={false}
                  animate={{ transform: at(node.x + 1.5, node.y + 1) }}
                  transition={{ duration: 0.8, ease: EASE_IN_OUT }}
                >
                  <motion.span
                    key={step}
                    className="absolute -left-3 -top-3 h-6 w-6 rounded-full border-2 border-[#ff00f2]"
                    initial={{ opacity: 0, transform: 'scale(0.4)' }}
                    animate={{ opacity: [0, 0.9, 0], transform: ['scale(0.4)', 'scale(0.6)', 'scale(1.6)'] }}
                    transition={{ duration: 0.55, delay: 0.75, ease: EASE_OUT, times: [0, 0.2, 1] }}
                  />
                  <svg viewBox="0 0 24 24" className="absolute left-0 top-0 h-6 w-6 drop-shadow-[0_2px_3px_rgb(0_0_0_/_0.35)]">
                    <path d="M3 2 L3 19 L7.6 14.6 L10.6 21.4 L13.6 20.1 L10.7 13.4 L17 13.4 Z" fill="#0a0a0a" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
                  </svg>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
